// Port of TrailBivvyKit/Sources/GPX/GPXParser.swift.
//
// The Swift parser is a SAX delegate of Foundation's `XMLParser` (libxml2). The browser has no streaming XML parser,
// so this port parses with `DOMParser` and replays the DOM in document order as the same start-element / characters /
// end-element events, driving a line-by-line port of the Swift delegate (`GPXParsingHandler`). Differences that
// follow from the DOM (documented in decisions and in the module's tests):
// - DOM nodes carry no line numbers. `invalidCoordinate.line` is recomputed from the source text: the line of the
//   `>` that ends the offending start tag, which is the line libxml2 reports in its start-element callback.
// - A parse error is all-or-nothing: the line comes from the parser's diagnostic (Chromium/WebKit "error on line N
//   at column C", Firefox "Line Number N, Column C", jsdom "N:C: …"); `1` when no position can be read. The message
//   is that diagnostic plus the tag at the error position, e.g. "unexpected close tag. (at </trkseg>)".
// - Swift streams, so a non-`<gpx>` root or an invalid coordinate that comes before a well-formedness error wins
//   over `malformedXML`. The port detects the non-`<gpx>` root case from the source text; an invalid coordinate
//   before a well-formedness error is reported as `malformedXML`.
// - libxml2 treats an undeclared namespace prefix (`<ext:name>` without `xmlns:ext`) as a recoverable namespace
//   error and XMLParser parses the file; DOMParser rejects it. When the first parse fails, the port declares the
//   missing prefixes on the root element (same line, so line numbers do not move) and parses again.

import { isValidCoordinate } from '../core/geo';
import { parseDate } from '../core/jsonCoding';
import type { EpochMs } from '../core/models';
import { GPXError, type GPXErrorReason } from './errors';
import { assignOptional, trimWhitespaceAndNewlines } from './internal';
import {
  XMLInputError,
  asBytes,
  containsNonXMLWhitespace,
  decodeXMLBytes,
  localName,
  parseXMLText,
  replay,
  rootIsNotBeforeError,
  startTagLine,
  utf8ByteLength,
  type XMLEventHandler,
} from './xml';
import {
  makeGPXDocument,
  type GPXDocument,
  type GPXRoute,
  type GPXTrack,
  type GPXTrackSegment,
  type GPXWaypoint,
} from './models';

/**
 * GPX reader.
 *
 * - Accepts GPX 1.0 and 1.1. Element matching is namespace-agnostic: only local names are compared, so
 *   default-namespace, prefixed (`<gpx:trkpt>`) and namespace-less files all parse.
 * - Tolerant: missing or unparseable `<ele>`/`<time>` become `undefined`, surrounding whitespace is trimmed,
 *   CDATA sections are read as text, and everything inside `<extensions>` (Garmin, Suunto…) is skipped.
 * - Strict where it matters: a point with a missing or out-of-range coordinate throws
 *   `GPXError` `invalidCoordinate` rather than being silently dropped or clamped.
 * - External entities are never resolved. Input larger than `maximumFileSize` (UTF-8 bytes) is rejected before
 *   parsing.
 *
 * The parser holds no state between calls. It needs `DOMParser` (browsers, or jsdom in tests).
 */
export class GPXParser {
  /** Largest accepted input, in bytes (UTF-8 bytes for a string input). */
  readonly maximumFileSize: number;

  /** Creates a parser. `maximumFileSize` defaults to 50 MB, far above any realistic multi-week hiking track. */
  constructor(maximumFileSize = 50_000_000) {
    this.maximumFileSize = maximumFileSize;
  }

  /**
   * Parses GPX text, or GPX bytes (encoding from the byte-order mark or the XML declaration, UTF-8 by default).
   * Throws `GPXError` (`fileTooLarge`, `malformedXML`, `notGPX` or `invalidCoordinate`), or an `Error` when no
   * `DOMParser` is available.
   */
  parse(data: string | Uint8Array | ArrayBuffer): GPXDocument {
    const input = typeof data === 'string' ? data : asBytes(data);
    const size = typeof input === 'string' ? utf8ByteLength(input) : input.byteLength;
    if (size > this.maximumFileSize) {
      throw new GPXError({ kind: 'fileTooLarge', bytes: size });
    }
    // Empty or whitespace-only input carries no root element at all.
    if (!containsNonXMLWhitespace(input)) throw new GPXError({ kind: 'notGPX' });

    let decoded: string;
    try {
      decoded = typeof input === 'string' ? input : decodeXMLBytes(input);
    } catch (e) {
      if (e instanceof XMLInputError) throw new GPXError({ kind: 'malformedXML', line: e.line, message: e.message });
      throw e;
    }
    // A byte-order mark is not part of the document (Swift's UTF-8 Data path strips it too).
    const text = decoded.charCodeAt(0) === 0xfeff ? decoded.slice(1) : decoded;

    const parsed = parseXMLText(text, 'GPXParser');
    if (parsed.kind === 'error') {
      if (rootIsNotBeforeError(parsed.source, parsed.error, 'gpx')) throw new GPXError({ kind: 'notGPX' });
      throw new GPXError({ kind: 'malformedXML', line: parsed.error.line ?? 1, message: parsed.error.message });
    }
    const root = parsed.document.documentElement;
    if (!root) throw new GPXError({ kind: 'notGPX' });

    const handler = new GPXParsingHandler((elementIndex) => startTagLine(parsed.source, elementIndex));
    replay(root, handler);
    if (handler.failure) throw new GPXError(handler.failure);
    if (!handler.sawGPXRoot) throw new GPXError({ kind: 'notGPX' });
    return handler.document;
  }
}

// MARK: Delegate (port of the Swift `GPXParsingHandler`)

type PointKind = 'waypoint' | 'routePoint' | 'trackPoint';

/** Builds a `GPXDocument` from start / characters / end events. Used by a single `parse` call only. */
class GPXParsingHandler implements XMLEventHandler {
  readonly document: GPXDocument = makeGPXDocument();
  failure?: GPXErrorReason;
  sawGPXRoot = false;

  /** Local names of the currently open elements (root first). */
  private readonly stack: string[] = [];
  /** Character data of the innermost open element. */
  private text = '';
  /** Depth inside an `<extensions>` subtree (0 = outside). */
  private extensionDepth = 0;

  private point?: GPXWaypoint;
  private pointKind?: PointKind;
  private route?: GPXRoute;
  private track?: GPXTrack;
  private segment?: GPXTrackSegment;

  /** `lineOfElement(i)`: 1-based line of the i-th start tag of the source (document order, root = 0). */
  constructor(private readonly lineOfElement: (elementIndex: number) => number) {}

  /** The DOM replay stops at the first failure (Swift aborts parsing). */
  get isStopped(): boolean {
    return this.failure !== undefined;
  }

  didStartElement(elementName: string, attribute: (name: string) => string | undefined, elementIndex: number): void {
    const name = localName(elementName);
    const parent = this.stack.length > 0 ? this.stack[this.stack.length - 1] : undefined;
    this.stack.push(name);
    this.text = '';

    if (parent === undefined) {
      if (name !== 'gpx') return this.fail({ kind: 'notGPX' });
      this.sawGPXRoot = true;
      assignOptional(this.document, 'creator', clean(attribute('creator')));
      return;
    }
    if (this.extensionDepth > 0) {
      this.extensionDepth += 1;
      return;
    }

    if (name === 'extensions') {
      this.extensionDepth = 1;
    } else if (name === 'wpt' && parent === 'gpx') {
      this.beginPoint('waypoint', attribute, elementIndex);
    } else if (name === 'rte' && parent === 'gpx') {
      this.route = { points: [] };
    } else if (name === 'rtept' && parent === 'rte' && this.route !== undefined) {
      this.beginPoint('routePoint', attribute, elementIndex);
    } else if (name === 'trk' && parent === 'gpx') {
      this.track = { segments: [] };
    } else if (name === 'trkseg' && parent === 'trk' && this.track !== undefined) {
      this.segment = { points: [] };
    } else if (name === 'trkpt' && this.track !== undefined && (parent === 'trkseg' || parent === 'trk')) {
      // Some writers omit <trkseg>; collect such points into an implicit segment.
      if (this.segment === undefined) this.segment = { points: [] };
      this.beginPoint('trackPoint', attribute, elementIndex);
    }
  }

  didEndElement(elementName: string): void {
    this.handleEnd(localName(elementName));
    if (this.stack.length > 0) this.stack.pop();
    this.text = '';
  }

  foundCharacters(characters: string): void {
    if (this.extensionDepth !== 0) return;
    this.text += characters;
  }

  private handleEnd(name: string): void {
    if (this.extensionDepth > 0) {
      this.extensionDepth -= 1;
      return;
    }
    const stack = this.stack;
    const parent = stack.length >= 2 ? stack[stack.length - 2] : undefined;
    const grandparent = stack.length >= 3 ? stack[stack.length - 3] : undefined;
    const point = this.point;
    const isPointChild = point !== undefined && (parent === 'wpt' || parent === 'rtept' || parent === 'trkpt');
    const isMetadataChild = parent === 'metadata' || parent === 'gpx';
    const metadata = this.document.metadata;
    const route = this.route;
    const track = this.track;
    const text = this.text;

    switch (name) {
      case 'wpt':
      case 'rtept':
      case 'trkpt':
        this.endPoint();
        break;
      case 'ele':
        if (isPointChild) assignOptional(point, 'elevation', parseNumber(text));
        break;
      case 'time':
        if (isPointChild) assignOptional(point, 'time', parseTime(text));
        else if (isMetadataChild) assignOptional(metadata, 'time', parseTime(text));
        break;
      case 'name':
        if (isPointChild) assignOptional(point, 'name', clean(text));
        else if (parent === 'rte') assignOptionalIn(route, 'name', clean(text));
        else if (parent === 'trk') assignOptionalIn(track, 'name', clean(text));
        else if (isMetadataChild) assignOptional(metadata, 'name', clean(text));
        else if (parent === 'author' && (grandparent === 'metadata' || grandparent === 'gpx')) {
          assignOptional(metadata, 'author', clean(text));
        }
        break;
      case 'author':
        // GPX 1.0: <author> holds the name directly. In GPX 1.1 its own text is only whitespace.
        if (isMetadataChild && metadata.author === undefined) assignOptional(metadata, 'author', clean(text));
        break;
      case 'cmt':
        if (isPointChild) assignOptional(point, 'comment', clean(text));
        break;
      case 'desc':
        if (isPointChild) assignOptional(point, 'description', clean(text));
        else if (parent === 'rte') assignOptionalIn(route, 'description', clean(text));
        else if (parent === 'trk') assignOptionalIn(track, 'description', clean(text));
        else if (isMetadataChild) assignOptional(metadata, 'description', clean(text));
        break;
      case 'sym':
        if (isPointChild) assignOptional(point, 'symbol', clean(text));
        break;
      case 'type':
        if (isPointChild) assignOptional(point, 'type', clean(text));
        else if (parent === 'trk') assignOptionalIn(track, 'type', clean(text));
        break;
      case 'trkseg':
        if (this.segment) this.track?.segments.push(this.segment);
        this.segment = undefined;
        break;
      case 'trk':
        if (this.segment) this.track?.segments.push(this.segment);
        this.segment = undefined;
        if (this.track) this.document.tracks.push(this.track);
        this.track = undefined;
        break;
      case 'rte':
        if (this.route) this.document.routes.push(this.route);
        this.route = undefined;
        break;
      default:
        break;
    }
  }

  // MARK: Points

  private beginPoint(kind: PointKind, attribute: (name: string) => string | undefined, elementIndex: number): void {
    const latitudeText = attribute('lat');
    const longitudeText = attribute('lon');
    const latitude = latitudeText === undefined ? undefined : parseNumber(latitudeText);
    const longitude = longitudeText === undefined ? undefined : parseNumber(longitudeText);
    if (latitude === undefined || longitude === undefined) {
      return this.fail({ kind: 'invalidCoordinate', line: Math.max(this.lineOfElement(elementIndex), 1) });
    }
    const coordinate = { latitude, longitude };
    if (!isValidCoordinate(coordinate)) {
      return this.fail({ kind: 'invalidCoordinate', line: Math.max(this.lineOfElement(elementIndex), 1) });
    }
    this.point = { coordinate };
    this.pointKind = kind;
  }

  private endPoint(): void {
    const finished = this.point;
    const kind = this.pointKind;
    if (finished === undefined || kind === undefined) return;
    switch (kind) {
      case 'waypoint':
        this.document.waypoints.push(finished);
        break;
      case 'routePoint':
        this.route?.points.push(finished);
        break;
      case 'trackPoint':
        this.segment?.points.push(finished);
        break;
    }
    this.point = undefined;
    this.pointKind = undefined;
  }

  private fail(reason: GPXErrorReason): void {
    if (this.failure === undefined) this.failure = reason;
  }
}

// MARK: Value parsing

/** Swift `optional?.property = value`: no effect when `target` is `undefined`. */
function assignOptionalIn<T extends object, K extends keyof T>(target: T | undefined, key: K, value: T[K] | undefined) {
  if (target !== undefined) assignOptional(target, key, value);
}


/** Trimmed text, or `undefined` when nothing is left (Swift `clean`). */
function clean(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const trimmed = trimWhitespaceAndNewlines(raw);
  return trimmed === '' ? undefined : trimmed;
}

/** Decimal floating-point literal as accepted by Swift's `Double(String)` (hexadecimal floats aside). */
const decimalNumber = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

/** A finite number from trimmed text, else `undefined` (Swift `number`: `Double(text)` and `isFinite`). */
function parseNumber(raw: string): number | undefined {
  const trimmed = trimWhitespaceAndNewlines(raw);
  if (!decimalNumber.test(trimmed)) return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/** ISO 8601 instant via `JSONCoding.parseDate` (Swift `date`). */
function parseTime(raw: string): EpochMs | undefined {
  const cleaned = clean(raw);
  return cleaned === undefined ? undefined : parseDate(cleaned);
}

